// Profile body, extracted from ProfileModal so the same implementation renders
// both inside the legacy standalone modal and as a section of the unified
// settings shell.
//
// This is deliberately NOT a mechanical move. Two behaviours are coupled to the
// enclosing dialog and must be preserved by any host:
//
//  1. All edit state resets when the section goes away. The trigger is the
//     `active` prop (formerly `isOpen`) — a host that keeps the section mounted
//     while showing something else MUST flip `active`, or stale draft/error/
//     success state leaks back when the user returns.
//  2. Escape while editing the display name cancels the EDIT, not the dialog.
//     That relies on a capture-phase native listener on the input pre-empting
//     Modal's bubble-phase document handler (see the comment inline). It keeps
//     working under any host that uses ui/Modal, and is covered by test.

import { useEffect, useRef, useState } from 'react'
import type { User } from '../../lib/gotrueTypes'
import { useTranslation } from 'react-i18next'
import { Check, ChevronDown } from 'lucide-react'
import { createNadDebug } from '../../utils/debugLog'
import { supabase } from '../../utils/supabase'
import { putBackendPreferredLocale } from '../../api/preferredLocale'
import { SUPPORTED_LOCALE_OPTIONS, normalizeLocale } from '../../i18n/supportedLocales'
import { trackInteraction } from '../../analytics/interactionAnalytics'

const log = createNadDebug('dw:account-self-service:profile-modal')

const MAX_NAME_LENGTH = 50
const RATE_LIMIT_COOLDOWN_MS = 30_000
const SUCCESS_REVERT_MS = 1500
const PROFILE_LANGUAGE_TRACE_ID = 'profile-language-dropdown-alignment'

export interface ProfileContentProps {
  /** Whether the section is on screen. Drives the state reset (was `isOpen`). */
  active: boolean
  user: User
  onChangePassword: () => void
  onDeleteAccount: () => void
}

export default function ProfileContent({
  active,
  user,
  onChangePassword,
  onDeleteAccount,
}: ProfileContentProps) {
  const { t, i18n } = useTranslation('profile')
  const currentFullName: string = user.user_metadata?.full_name || ''
  const displayName = currentFullName || t('userFallbackName')
  const email = user.email || ''
  // The active UI language is the source of truth for the picker's current
  // selection. LocaleSync reconciles it with the backend-owned table on auth,
  // so we no longer read the (deprecated-as-a-store) user_metadata locale here.
  const currentLocale: string = normalizeLocale(i18n.language) || 'en'

  const [editing, setEditing] = useState(false)
  const [newName, setNewName] = useState(currentFullName)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [languageError, setLanguageError] = useState<string | null>(null)
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false)
  const [languageChangePending, setLanguageChangePending] = useState(false)
  const [cooldownUntil, setCooldownUntil] = useState<number | null>(null)
  // ``cooldownTick`` is bumped once per second while the cooldown is
  // active; its sole purpose is to force a re-render so the
  // ``Try again in Ns`` label visibly counts down. We cannot rely on
  // re-setting ``cooldownUntil`` to the same value — React bails
  // out of same-value state updates.
  const [, setCooldownTick] = useState(0)
  const cooldownTimer = useRef<ReturnType<typeof setInterval> | null>(null)
  const revertTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const languageMenuRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (active) {
      log('open user=%s', user.email)
    }
  }, [active, user.email])

  // Reset edit state whenever the modal closes.
  useEffect(() => {
    if (!active) {
      setEditing(false)
      setNewName(currentFullName)
      setSubmitting(false)
      setError(null)
      setSuccess(false)
      setLanguageError(null)
      setLanguageMenuOpen(false)
      if (revertTimer.current) {
        clearTimeout(revertTimer.current)
        revertTimer.current = null
      }
    }
  }, [active, currentFullName])

  useEffect(() => {
    if (!languageMenuOpen) return

    const closeForOutsidePointer = (event: MouseEvent) => {
      const target = event.target
      if (!(target instanceof Node)) return
      if (languageMenuRef.current?.contains(target)) return
      log({
        event: 'locale-menu-close',
        trace_id: PROFILE_LANGUAGE_TRACE_ID,
        reason: 'outside',
      })
      setLanguageMenuOpen(false)
    }

    const closeForEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      event.stopImmediatePropagation()
      log({
        event: 'locale-menu-close',
        trace_id: PROFILE_LANGUAGE_TRACE_ID,
        reason: 'escape',
      })
      setLanguageMenuOpen(false)
    }

    document.addEventListener('mousedown', closeForOutsidePointer)
    document.addEventListener('keydown', closeForEscape, true)
    return () => {
      document.removeEventListener('mousedown', closeForOutsidePointer)
      document.removeEventListener('keydown', closeForEscape, true)
    }
  }, [languageMenuOpen])

  // Cleanup timers on unmount.
  useEffect(() => {
    return () => {
      if (cooldownTimer.current) clearInterval(cooldownTimer.current)
      if (revertTimer.current) clearTimeout(revertTimer.current)
    }
  }, [])

  // Cooldown ticker. Once per second: either clear the cooldown
  // (deadline passed) or bump ``cooldownTick`` so the
  // ``Try again in Ns`` label re-renders. Unlike
  // ``ChangePasswordModal`` which re-sets ``cooldownUntil`` to its
  // current value (a no-op under React's bail-out optimization),
  // we use a dedicated tick counter to guarantee a re-render.
  useEffect(() => {
    if (cooldownUntil === null) return
    cooldownTimer.current = setInterval(() => {
      if (cooldownUntil <= Date.now()) {
        setCooldownUntil(null)
        if (cooldownTimer.current) clearInterval(cooldownTimer.current)
      } else {
        setCooldownTick((n) => n + 1)
      }
    }, 1000)
    return () => {
      if (cooldownTimer.current) clearInterval(cooldownTimer.current)
    }
  }, [cooldownUntil])

  // Capture-phase native listener on the input: when the user presses
  // Esc while editing the name we want to cancel the edit ONLY — not
  // close the whole Profile modal. ``Modal`` installs a bubble-phase
  // document-level Esc handler (Modal.tsx:60), so we must intercept
  // the event in the capture phase (which fires before document
  // bubble) and call ``stopImmediatePropagation()`` to prevent Modal
  // from also acting on it. Using React's ``onKeyDown`` is too late —
  // React's synthetic events are delegated through the root container
  // and run after document-level native bubble listeners have already
  // fired.
  const inputRef = useRef<HTMLInputElement | null>(null)
  useEffect(() => {
    if (!editing) return
    const el = inputRef.current
    if (!el) return
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.preventDefault()
      e.stopImmediatePropagation()
      // Mirror the Cancel button's disabled-while-submitting gate —
      // letting Esc cancel mid-flight would re-open the race window
      // documented on the Cancel button below.
      if (submitting) return
      cancelEdit()
    }
    el.addEventListener('keydown', handler, true)
    return () => el.removeEventListener('keydown', handler, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, submitting])

  const cooldownActive = cooldownUntil !== null && cooldownUntil > Date.now()
  const cooldownRemaining = cooldownActive
    ? Math.max(0, Math.ceil((cooldownUntil! - Date.now()) / 1000))
    : 0

  const trimmed = newName.trim()
  const saveDisabled =
    submitting ||
    cooldownActive ||
    trimmed.length === 0 ||
    trimmed.length > MAX_NAME_LENGTH ||
    trimmed === currentFullName

  const startEdit = () => {
    log('edit-name:start oldName=%s', currentFullName)
    setNewName(currentFullName)
    setError(null)
    setSuccess(false)
    setEditing(true)
  }

  const cancelEdit = () => {
    log('edit-name:cancel')
    setEditing(false)
    setNewName(currentFullName)
    setError(null)
    setSuccess(false)
    setSubmitting(false)
    if (revertTimer.current) {
      clearTimeout(revertTimer.current)
      revertTimer.current = null
    }
  }

  const handleSubmit = async () => {
    const next = newName.trim()
    if (
      submitting ||
      cooldownActive ||
      next.length === 0 ||
      next.length > MAX_NAME_LENGTH ||
      next === currentFullName
    ) {
      return
    }
    setSubmitting(true)
    setError(null)
    setSuccess(false)
    log('edit-name:submit start oldName=%s newNameLen=%d', currentFullName, next.length)
    try {
      const { error: updateError } = await supabase.auth.updateUser({
        data: { full_name: next },
      })
      if (updateError) {
        throw updateError
      }
      log('edit-name:submit success')
      trackInteraction('settings_profile_changed', { setting_key: 'display_name' })
      setSuccess(true)
      setSubmitting(false)
      revertTimer.current = setTimeout(() => {
        setEditing(false)
        setSuccess(false)
        revertTimer.current = null
      }, SUCCESS_REVERT_MS)
    } catch (err) {
      const errObj = (err instanceof Object ? err : {}) satisfies object
      const status =
        'status' in errObj && typeof errObj.status === 'number' ? errObj.status : undefined
      const message = err instanceof Error ? err.message : t('failedToUpdateName')
      log('edit-name:submit error status=%s message=%s', status, message)
      if (status === 429) {
        setCooldownUntil(Date.now() + RATE_LIMIT_COOLDOWN_MS)
        log('edit-name:rate-limit-cooldown ms=%d', RATE_LIMIT_COOLDOWN_MS)
        // Keep the provider's own reason alongside our summary. Replacing it
        // with a generic string leaves the user (and anyone reading a bug
        // report) unable to tell WHY the write was refused — see CLAUDE.md
        // principle 7 on surfacing provider errors faithfully.
        setError(
          message
            ? t('errorWithReason', { summary: t('tooManyRequests'), reason: message })
            : t('tooManyRequests'),
        )
      } else {
        setError(message)
      }
      setSubmitting(false)
    }
  }

  // Enter -> submit. Escape is handled by the capture-phase native
  // listener installed above (so it can pre-empt Modal's document
  // listener); keeping Esc here would be a no-op at best and a
  // duplicate cancel at worst.
  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      void handleSubmit()
    }
  }

  const saveLabel = submitting
    ? t('saving')
    : cooldownActive
      ? t('tryAgainIn', { seconds: cooldownRemaining })
      : t('save')

  const currentLocaleOption =
    SUPPORTED_LOCALE_OPTIONS.find(({ code }) => code === currentLocale) ??
    SUPPORTED_LOCALE_OPTIONS[0]

  const handleLanguageChange = async (lng: string) => {
    if (languageChangePending) {
      log({ event: 'locale-change-blocked', trace_id: PROFILE_LANGUAGE_TRACE_ID, locale: lng })
      return
    }
    setLanguageChangePending(true)
    log({
      event: 'locale-change-start',
      trace_id: PROFILE_LANGUAGE_TRACE_ID,
      from: i18n.language,
      to: lng,
    })
    setLanguageError(null)
    try {
      // Persist to the backend-owned table (single source of truth) and WAIT for
      // it before applying, so the table is never stale relative to the UI. This
      // replaced the former GoTrue user_metadata write. If the write fails, we do
      // not switch the UI — the persisted preference and the visible language
      // stay in sync.
      await putBackendPreferredLocale(lng)
      await i18n.changeLanguage(lng)
      log({ event: 'locale-change-success', trace_id: PROFILE_LANGUAGE_TRACE_ID, locale: lng })
    } catch (err) {
      const message = err instanceof Error ? err.message : ''
      log({ event: 'locale-change-error', trace_id: PROFILE_LANGUAGE_TRACE_ID, message })
      // Same reasoning: the backend's reason was being logged and then thrown
      // away before it reached the user.
      setLanguageError(
        message
          ? t('errorWithReason', { summary: t('failedToUpdateLanguage'), reason: message })
          : t('failedToUpdateLanguage'),
      )
    } finally {
      setLanguageChangePending(false)
    }
  }

  const handleLanguageOptionSelect = (lng: string) => {
    setLanguageMenuOpen(false)
    if (languageChangePending) {
      log({ event: 'locale-menu-select-blocked', trace_id: PROFILE_LANGUAGE_TRACE_ID, locale: lng })
      return
    }
    if (lng === currentLocale) {
      log({
        event: 'locale-menu-select-current',
        trace_id: PROFILE_LANGUAGE_TRACE_ID,
        locale: lng,
      })
      return
    }
    log({ event: 'locale-menu-select', trace_id: PROFILE_LANGUAGE_TRACE_ID, locale: lng })
    if (lng === 'en' || lng === 'ko' || lng === 'vi') trackInteraction('settings_profile_changed', { setting_key: 'ui_locale', ui_locale: lng })
    void handleLanguageChange(lng)
  }

  return (
    <div className="flex flex-col gap-4" data-testid="profile-modal">
      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {t('displayName')}
        </label>
        {editing ? (
          <div className="flex flex-col gap-2">
            <input
              ref={inputRef}
              data-testid="profile-name-input"
              type="text"
              autoFocus
              maxLength={MAX_NAME_LENGTH}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={handleInputKeyDown}
              className="px-3 py-2 rounded-md bg-surface-2 border border-border text-sm text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            />
            {error && (
              <div className="text-xs font-medium text-destructive" data-testid="profile-name-error">
                {error}
              </div>
            )}
            {success && (
              <div
                className="text-xs font-medium text-success"
                data-testid="profile-name-success"
              >
                {t('saved')}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                data-testid="profile-name-cancel"
                onClick={cancelEdit}
                // Disable while submitting so the user cannot start a
                // second edit while the first PUT is in flight (which
                // would race — whichever response landed last would
                // win, potentially overwriting the later edit with the
                // earlier "cancelled" value).
                disabled={submitting}
                className="px-3 py-2 rounded-md text-sm bg-surface-2 hover:bg-surface-3 text-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              >
                {t('cancel')}
              </button>
              <button
                type="button"
                data-testid="profile-name-save"
                onClick={() => {
                  void handleSubmit()
                }}
                disabled={saveDisabled}
                className="px-3 py-2 rounded-md text-sm bg-primary hover:bg-primary-dark text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              >
                {saveLabel}
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <div className="text-sm text-foreground" data-testid="profile-name">
              {displayName}
            </div>
            <button
              type="button"
              data-testid="profile-name-edit"
              aria-label={t('editDisplayName')}
              onClick={startEdit}
              className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-surface-2 transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </button>
          </div>
        )}
      </div>
      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {t('email')}
        </label>
        <div className="text-sm text-foreground break-all" data-testid="profile-email">
          {email}
        </div>
      </div>
      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {t('language')}
        </label>
        <div ref={languageMenuRef} className="relative">
          <button
            type="button"
            data-testid="profile-language-select"
            aria-haspopup="listbox"
            aria-expanded={languageMenuOpen}
            aria-busy={languageChangePending}
            disabled={languageChangePending}
            onClick={() => {
              if (languageChangePending) return
              const nextOpen = !languageMenuOpen
              log({
                event: nextOpen ? 'locale-menu-open' : 'locale-menu-close',
                trace_id: PROFILE_LANGUAGE_TRACE_ID,
                locale: currentLocale,
                reason: 'trigger',
              })
              setLanguageMenuOpen(nextOpen)
            }}
            className="flex h-9 w-full items-center justify-between rounded-lg border border-input-border bg-input-bg px-3 text-sm text-foreground transition-colors hover:border-input-border-hover hover:bg-surface-3 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <span>{currentLocaleOption.label}</span>
            <ChevronDown
              className={`h-4 w-4 text-muted-foreground transition-transform ${languageMenuOpen ? 'rotate-180' : ''}`}
              strokeWidth={2}
            />
          </button>
          {languageMenuOpen && (
            <div
              role="listbox"
              data-testid="profile-language-menu"
              aria-label={t('language')}
              className="absolute left-0 right-0 top-full z-50 mt-1 rounded-lg border border-input-border bg-background py-1 shadow-xl"
            >
              {SUPPORTED_LOCALE_OPTIONS.map(({ code, label }) => {
                const selected = code === currentLocale
                return (
                  <button
                    key={code}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    disabled={languageChangePending}
                    data-testid={`profile-language-option-${code}`}
                    onClick={() => handleLanguageOptionSelect(code)}
                    className={`flex h-8 w-full items-center justify-between border border-transparent px-3 text-left text-sm transition-colors hover:border-input-border-hover hover:bg-surface-3 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-60 ${selected ? 'bg-primary/10 text-primary' : 'text-foreground'}`}
                  >
                    <span>{label}</span>
                    {selected && <Check className="h-4 w-4" strokeWidth={2} />}
                  </button>
                )
              })}
            </div>
          )}
        </div>
        {languageError && (
          <div className="text-xs font-medium text-destructive" data-testid="profile-language-error">
            {languageError}
          </div>
        )}
      </div>
      <div className="flex flex-col gap-2 mt-2">
        <button
          type="button"
          data-testid="profile-change-password"
          onClick={() => {
            log('click change-password')
            onChangePassword()
          }}
          className="w-full px-4 py-2.5 rounded-md text-sm font-medium bg-surface-2 hover:bg-surface-3 text-foreground transition-colors text-left cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        >
          {t('changePassword')}
        </button>
        <button
          type="button"
          data-testid="profile-delete-account"
          onClick={() => {
            log('click delete-account')
            onDeleteAccount()
          }}
          className="w-full px-4 py-2.5 rounded-md text-sm font-medium bg-transparent hover:bg-destructive/10 text-destructive transition-colors text-left cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        >
          {t('deleteAccount')}
        </button>
      </div>
    </div>

  )
}
